import * as fileApi from '@/api/file';
import { FileEditorMapService } from '@/utils/fileEditorMapService';
import { fileEventQueue, FileEventType } from '@/utils/fileEvent';
import { fileService, getLanguageByFileName } from '@/utils/filesService';
import { Pane, rootOfPath } from '@/utils/fileStore';
import { Database as editorDb } from '@/utils/persistence';
import { useThemeStore } from '@/utils/themeStore';
import Editor, { loader } from '@monaco-editor/react';
import { Breadcrumb, Button, Checkbox, Input } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSize } from './resizableContainer';
import ResizableContainer from './resizableContainer'
import useFileStore from '@/utils/fileStore'

const LARGE_FILE_CHUNK_THRESHOLD = 512 * 1024;
const FILE_CHUNK_SIZE = 128 * 1024;
const LOAD_MORE_SCROLL_THRESHOLD_PX = 200;
const LOAD_PREV_SCROLL_THRESHOLD_PX = 120;
const CHUNK_SCROLL_TRIGGER_COOLDOWN_MS = 120;
const REMOTE_SEARCH_RESULT_LIMIT = 100;
const REMOTE_SEARCH_DEBOUNCE_MS = 350;
const MAX_CHUNK_WINDOW_COUNT = 6;
const EDITOR_TOOLBAR_HEIGHT = 32;
const LARGE_FILE_EXTRA_TOOLBAR_HEIGHT = 32;
const EDITOR_MIN_HEIGHT = 120;

loader.config({
    paths: {
        vs: import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL + '/vs' : window.origin + '/vs',
    }
});

interface FileEditorProps {
    path?: string;
    pane: Pane

    fileName?: string;
    onChange?: (value?: string) => void;
}

export interface FileEditorRef {
    getContent: () => string | undefined;
    refresh: () => Promise<void>;
}

type LoadedChunk = {
    offset: number;
    bytes: number;
    text: string;
};


const FileEditor = forwardRef<FileEditorRef, FileEditorProps>(({
    path,
    pane,
    onChange,
    fileName
}: FileEditorProps, ref) => {
    const size = useSize();
    const theme = useThemeStore((state) => state.theme);
    const [content, setContent] = useState<string>();
    const [language, setLanguage] = useState('plaintext');
    const [isLoading, setIsLoading] = useState(false);
    const [loadedBytes, setLoadedBytes] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);
    const [isLargeFile, setIsLargeFile] = useState(false);
    const getFileNode = useFileStore(state => state.getFileNode);
    const roots = useFileStore(state => state.roots);
    const editorRef = useRef<any>(null);
    const isChunkModeRef = useRef(false);
    const chunkPathRef = useRef<string | undefined>(undefined);
    const chunkStartOffsetRef = useRef(0);
    const chunkEndOffsetRef = useRef(0);
    const chunkTotalSizeRef = useRef(0);
    const hasMoreChunkRef = useRef(false);
    const hasPrevChunkRef = useRef(false);
    const isChunkLoadingRef = useRef(false);
    const loadedChunksRef = useRef<LoadedChunk[]>([]);
    const lastEditorScrollTopRef = useRef(0);
    const lastPrevLoadTriggerAtRef = useRef(0);
    const lastNextLoadTriggerAtRef = useRef(0);
    const suppressScrollAutoLoadCountRef = useRef(0);
    const remoteSearchMatchesRef = useRef<fileApi.SearchRawFileMatch[]>([]);
    const remoteSearchCurrentIndexRef = useRef(0);
    const remoteSearchDecorationIdsRef = useRef<string[]>([]);
    const remoteSearchCurrentDecorationIdsRef = useRef<string[]>([]);
    const remoteSearchRequestSeqRef = useRef(0);
    const remoteSearchDebounceTimerRef = useRef<number | undefined>(undefined);
    const remoteSearchInputRef = useRef<any>(null);
    const [remoteSearchQuery, setRemoteSearchQuery] = useState('');
    const [remoteSearchCaseSensitive, setRemoteSearchCaseSensitive] = useState(false);
    const [remoteSearchWholeWord, setRemoteSearchWholeWord] = useState(false);
    const [remoteSearchRegex, setRemoteSearchRegex] = useState(false);
    const [remoteSearchTotal, setRemoteSearchTotal] = useState(0);
    const [remoteSearchCurrent, setRemoteSearchCurrent] = useState(0);
    const [isRemoteSearching, setIsRemoteSearching] = useState(false);
    const [isJumpingToEnd, setIsJumpingToEnd] = useState(false);
    const [isWordWrap, setIsWordWrap] = useState(() => {
        // 检测是否为移动设备（屏幕宽度小于768px）
        return window.innerWidth < 768;
    });

    const getMatchRange = (model: any, match: fileApi.SearchRawFileMatch, fallbackLength: number) => {
        const lineCount = model.getLineCount?.() ?? 0;
        if (lineCount <= 0) return undefined;

        if (typeof match.offset === 'number' && match.offset >= 0) {
            const modelLength = model.getValueLength?.() ?? 0;
            const safeOffset = isChunkModeRef.current
                ? Math.min(
                    Math.max(match.offset - chunkStartOffsetRef.current, 0),
                    modelLength
                )
                : Math.min(match.offset, modelLength);
            const start = model.getPositionAt(safeOffset);
            const end = model.getPositionAt(Math.min(safeOffset + Math.max(fallbackLength, 1), modelLength));
            return {
                startLineNumber: start.lineNumber,
                startColumn: start.column,
                endLineNumber: end.lineNumber,
                endColumn: end.column
            };
        }

        if (
            typeof match.lineNumber === 'number' &&
            match.lineNumber >= 1 &&
            match.lineNumber <= lineCount
        ) {
            const lineMaxColumn = model.getLineMaxColumn(match.lineNumber);
            const startColumn = Math.min(Math.max(match.startColumn ?? 1, 1), lineMaxColumn);
            const endColumn = Math.min(
                Math.max(match.endColumn ?? (startColumn + fallbackLength), startColumn + 1),
                lineMaxColumn
            );
            return {
                startLineNumber: match.lineNumber,
                startColumn,
                endLineNumber: match.lineNumber,
                endColumn: endColumn > startColumn ? endColumn : Math.min(startColumn + 1, lineMaxColumn)
            };
        }

        return undefined;
    };

    const clearRemoteSearchDecorations = () => {
        const editor = editorRef.current;
        if (!editor) return;

        remoteSearchDecorationIdsRef.current = editor.deltaDecorations(remoteSearchDecorationIdsRef.current, []);
        remoteSearchCurrentDecorationIdsRef.current = editor.deltaDecorations(remoteSearchCurrentDecorationIdsRef.current, []);
    };

    const applyRemoteSearchDecorations = (query: string) => {
        const editor = editorRef.current;
        const model = editor?.getModel?.();
        if (!editor || !model) return;

        const fallbackLength = Math.max(query.length, 1);
        const allDecorations = remoteSearchMatchesRef.current
            .map(match => getMatchRange(model, match, fallbackLength))
            .filter(Boolean)
            .map(range => ({
                range,
                options: {
                    inlineClassName: 'findMatch'
                }
            }));

        remoteSearchDecorationIdsRef.current = editor.deltaDecorations(remoteSearchDecorationIdsRef.current, allDecorations);

        const currentMatch = remoteSearchMatchesRef.current[remoteSearchCurrentIndexRef.current];
        const currentRange = currentMatch ? getMatchRange(model, currentMatch, fallbackLength) : undefined;
        const currentDecorations = currentRange ? [{
            range: currentRange,
            options: {
                inlineClassName: 'currentFindMatch'
            }
        }] : [];

        remoteSearchCurrentDecorationIdsRef.current = editor.deltaDecorations(remoteSearchCurrentDecorationIdsRef.current, currentDecorations);
    };

    const clearRemoteSearchState = () => {
        remoteSearchMatchesRef.current = [];
        remoteSearchCurrentIndexRef.current = 0;
        remoteSearchRequestSeqRef.current += 1;
        if (remoteSearchDebounceTimerRef.current) {
            window.clearTimeout(remoteSearchDebounceTimerRef.current);
            remoteSearchDebounceTimerRef.current = undefined;
        }
        setRemoteSearchTotal(0);
        setRemoteSearchCurrent(0);

        clearRemoteSearchDecorations();
    };

    const loadChunkAtOffsetDirectly = async (targetOffset: number) => {
        if (!isChunkModeRef.current || !chunkPathRef.current) {
            return false;
        }

        const totalSize = chunkTotalSizeRef.current || totalBytes;
        if (totalSize <= 0) {
            return false;
        }

        const normalizedOffset = Math.max(0, Math.min(targetOffset, Math.max(totalSize - 1, 0)));
        const chunkOffset = Math.floor(normalizedOffset / FILE_CHUNK_SIZE) * FILE_CHUNK_SIZE;

        isChunkLoadingRef.current = true;
        setIsLoading(true);
        const chunkResponse = await fileApi.getRawFileResourceChunk({
            path: chunkPathRef.current,
            offset: chunkOffset,
            limit: FILE_CHUNK_SIZE
        });
        isChunkLoadingRef.current = false;
        setIsLoading(false);

        if (!chunkResponse) {
            return false;
        }

        chunkTotalSizeRef.current = chunkResponse.totalSize;
        const chunkText = atob(chunkResponse.content ?? '');
        loadedChunksRef.current = [{
            offset: chunkResponse.offset,
            bytes: chunkResponse.bytesRead,
            text: chunkText
        }];

        syncChunkWindowContent();
        setLoadedBytes(chunkEndOffsetRef.current);
        setTotalBytes(chunkResponse.totalSize);
        return true;
    };

    const runWithScrollAutoLoadSuspended = async (task: () => Promise<void>) => {
        suppressScrollAutoLoadCountRef.current += 1;
        try {
            await task();
        } finally {
            suppressScrollAutoLoadCountRef.current = Math.max(0, suppressScrollAutoLoadCountRef.current - 1);
        }
    };

    const ensureChunkLoadedToOffset = async (targetOffset: number) => {
        if (!isChunkModeRef.current) {
            return true;
        }

        const alreadyLoaded = targetOffset >= chunkStartOffsetRef.current && targetOffset < chunkEndOffsetRef.current;
        if (alreadyLoaded) {
            return true;
        }

        const loaded = await loadChunkAtOffsetDirectly(targetOffset);
        if (!loaded) {
            return false;
        }

        return targetOffset >= chunkStartOffsetRef.current && targetOffset < chunkEndOffsetRef.current;
    };

    const focusRemoteMatchByIndex = async (index: number, query: string) => {
        await runWithScrollAutoLoadSuspended(async () => {
            const editor = editorRef.current;
            if (!editor) {
                return;
            }

            const matches = remoteSearchMatchesRef.current;
            if (matches.length === 0) {
                return;
            }

            const normalizedIndex = ((index % matches.length) + matches.length) % matches.length;
            remoteSearchCurrentIndexRef.current = normalizedIndex;
            setRemoteSearchCurrent(normalizedIndex + 1);

            const target = matches[normalizedIndex];
            if (typeof target.offset === 'number' && target.offset >= 0) {
                await ensureChunkLoadedToOffset(target.offset);
            }

            const model = editor.getModel?.();
            if (!model) {
                return;
            }

            const fallbackLength = Math.max(query.length, 1);
            const range = getMatchRange(model, target, fallbackLength);
            if (!range) return;

            editor.revealRangeInCenter(range);
            editor.setPosition({
                lineNumber: range.startLineNumber,
                column: range.startColumn
            });
            editor.setSelection(range);
            editor.focus();
            applyRemoteSearchDecorations(query);
        });
    };

    const navigateRemoteMatch = async (delta: number, query: string) => {
        const matches = remoteSearchMatchesRef.current;
        if (!matches.length) {
            return;
        }

        const nextIndex = remoteSearchCurrentIndexRef.current + delta;
        await focusRemoteMatchByIndex(nextIndex, query);
    };

    const runRemoteSearch = async () => {
        if (!isChunkModeRef.current || !path || !editorRef.current) {
            return;
        }

        if (remoteSearchDebounceTimerRef.current) {
            window.clearTimeout(remoteSearchDebounceTimerRef.current);
            remoteSearchDebounceTimerRef.current = undefined;
        }

        const query = remoteSearchQuery.trim();
        if (!query) {
            clearRemoteSearchState();
            return;
        }

        setIsRemoteSearching(true);
        const requestSeq = ++remoteSearchRequestSeqRef.current;
        const result = await fileApi.searchRawFileResource({
            path,
            query,
            caseSensitive: remoteSearchCaseSensitive,
            wholeWord: remoteSearchWholeWord,
            regex: remoteSearchRegex,
            limit: REMOTE_SEARCH_RESULT_LIMIT
        });

        setIsRemoteSearching(false);

        if (requestSeq !== remoteSearchRequestSeqRef.current) {
            return;
        }

        if (!result || !result.matches?.length) {
            clearRemoteSearchState();
            return;
        }

        remoteSearchMatchesRef.current = result.matches;
        remoteSearchCurrentIndexRef.current = 0;
        setRemoteSearchTotal(result.total || result.matches.length);
        setRemoteSearchCurrent(1);

        if (requestSeq !== remoteSearchRequestSeqRef.current) {
            return;
        }

        await focusRemoteMatchByIndex(0, query);
    };

    const handleRemoteSearchPrev = async () => {
        const query = remoteSearchQuery.trim();
        if (!query) return;
        await navigateRemoteMatch(-1, query);
    };

    const handleRemoteSearchNext = async () => {
        const query = remoteSearchQuery.trim();
        if (!query) return;
        await navigateRemoteMatch(1, query);
    };

    const revealEditorEnd = () => {
        const editor = editorRef.current;
        const model = editor?.getModel?.();
        if (!editor || !model) {
            return;
        }

        const lastLineNumber = model.getLineCount?.() ?? 1;
        const lastColumn = model.getLineMaxColumn?.(lastLineNumber) ?? 1;
        const range = {
            startLineNumber: lastLineNumber,
            startColumn: lastColumn,
            endLineNumber: lastLineNumber,
            endColumn: lastColumn
        };

        editor.revealRangeInCenter(range);
        editor.setPosition({ lineNumber: lastLineNumber, column: lastColumn });
        editor.focus();
    };

    const loadLastChunkDirectly = async () => {
        if (!isChunkModeRef.current || !chunkPathRef.current) {
            return;
        }

        const knownTotalSize = chunkTotalSizeRef.current || totalBytes;
        if (knownTotalSize <= 0) {
            return;
        }

        const lastChunkOffset = Math.max(knownTotalSize - FILE_CHUNK_SIZE, 0);
        setIsLoading(true);
        const chunkResponse = await fileApi.getRawFileResourceChunk({
            path: chunkPathRef.current,
            offset: lastChunkOffset,
            limit: FILE_CHUNK_SIZE
        });
        setIsLoading(false);

        if (!chunkResponse) {
            return;
        }

        const chunkText = atob(chunkResponse.content ?? '');
        isChunkLoadingRef.current = false;
        loadedChunksRef.current = [{
            offset: lastChunkOffset,
            bytes: chunkResponse.bytesRead,
            text: chunkText
        }];
        chunkStartOffsetRef.current = lastChunkOffset;
        chunkEndOffsetRef.current = lastChunkOffset + chunkResponse.bytesRead;
        chunkTotalSizeRef.current = chunkResponse.totalSize;
        hasPrevChunkRef.current = lastChunkOffset > 0;
        hasMoreChunkRef.current = chunkEndOffsetRef.current < chunkResponse.totalSize;
        setLoadedBytes(chunkEndOffsetRef.current);
        setTotalBytes(chunkResponse.totalSize);
        setContent(chunkText);
        clearRemoteSearchState();
    };

    const scrollToFileEnd = async () => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        setIsJumpingToEnd(true);
        try {
            if (isChunkModeRef.current) {
                await loadLastChunkDirectly();
                await new Promise(resolve => window.requestAnimationFrame(() => resolve(undefined)));
            }
            revealEditorEnd();
        } finally {
            setIsJumpingToEnd(false);
        }
    };

    // const showUpdateMessageRef = React.useRef(false);

    const resetChunkState = () => {
        isChunkModeRef.current = false;
        chunkPathRef.current = undefined;
        chunkStartOffsetRef.current = 0;
        chunkEndOffsetRef.current = 0;
        chunkTotalSizeRef.current = 0;
        hasMoreChunkRef.current = false;
        hasPrevChunkRef.current = false;
        isChunkLoadingRef.current = false;
        loadedChunksRef.current = [];
        lastEditorScrollTopRef.current = 0;
        lastPrevLoadTriggerAtRef.current = 0;
        lastNextLoadTriggerAtRef.current = 0;
        clearRemoteSearchState();
        setLoadedBytes(0);
        setTotalBytes(0);
    };

    const syncChunkWindowContent = () => {
        const chunks = loadedChunksRef.current;
        if (!chunks.length) {
            chunkStartOffsetRef.current = 0;
            chunkEndOffsetRef.current = 0;
            setContent('');
            return;
        }

        const first = chunks[0];
        const last = chunks[chunks.length - 1];
        chunkStartOffsetRef.current = first.offset;
        chunkEndOffsetRef.current = last.offset + last.bytes;
        hasPrevChunkRef.current = chunkStartOffsetRef.current > 0;
        hasMoreChunkRef.current = chunkEndOffsetRef.current < chunkTotalSizeRef.current;
        setContent(chunks.map(item => item.text).join(''));
    };

    const loadPrevChunk = async () => {
        if (!isChunkModeRef.current || !chunkPathRef.current) return;
        if (!hasPrevChunkRef.current || isChunkLoadingRef.current) return;

        const editor = editorRef.current;
        const scrollTopBefore = editor?.getScrollTop?.() ?? 0;
        const scrollHeightBefore = editor?.getScrollHeight?.() ?? 0;
        const currentTopChunk = loadedChunksRef.current[0];
        const currentStart = currentTopChunk?.offset ?? chunkStartOffsetRef.current;
        const prevOffset = Math.max(currentStart - FILE_CHUNK_SIZE, 0);
        const prevLimit = currentStart - prevOffset;
        if (prevLimit <= 0) {
            hasPrevChunkRef.current = false;
            return;
        }

        isChunkLoadingRef.current = true;
        setIsLoading(true);
        const chunkResponse = await fileApi.getRawFileResourceChunk({
            path: chunkPathRef.current,
            offset: prevOffset,
            limit: prevLimit
        });
        isChunkLoadingRef.current = false;
        setIsLoading(false);

        if (!chunkResponse) {
            return;
        }

        const chunkText = atob(chunkResponse.content ?? '');
        chunkTotalSizeRef.current = chunkResponse.totalSize;
        loadedChunksRef.current = [
            {
                offset: chunkResponse.offset,
                bytes: chunkResponse.bytesRead,
                text: chunkText
            },
            ...loadedChunksRef.current
        ];
        if (loadedChunksRef.current.length > MAX_CHUNK_WINDOW_COUNT) {
            loadedChunksRef.current = loadedChunksRef.current.slice(0, MAX_CHUNK_WINDOW_COUNT);
        }

        syncChunkWindowContent();
        setLoadedBytes(chunkEndOffsetRef.current);
        setTotalBytes(chunkResponse.totalSize);

        if (editor) {
            window.requestAnimationFrame(() => {
                const scrollHeightAfter = editor.getScrollHeight?.() ?? scrollHeightBefore;
                const scrollDelta = Math.max(0, scrollHeightAfter - scrollHeightBefore);
                editor.setScrollTop(scrollTopBefore + scrollDelta);
            });
        }
    };

    const loadNextChunk = async () => {
        if (!isChunkModeRef.current || !chunkPathRef.current) return;
        if (!hasMoreChunkRef.current || isChunkLoadingRef.current) return;

        isChunkLoadingRef.current = true;
        setIsLoading(true);
        const chunkResponse = await fileApi.getRawFileResourceChunk({
            path: chunkPathRef.current,
            offset: chunkEndOffsetRef.current,
            limit: FILE_CHUNK_SIZE
        });
        isChunkLoadingRef.current = false;
        setIsLoading(false);

        if (!chunkResponse) {
            return;
        }

        chunkTotalSizeRef.current = chunkResponse.totalSize;
        const chunkText = atob(chunkResponse.content ?? '');
        loadedChunksRef.current = [
            ...loadedChunksRef.current,
            {
                offset: chunkResponse.offset,
                bytes: chunkResponse.bytesRead,
                text: chunkText
            }
        ];
        if (loadedChunksRef.current.length > MAX_CHUNK_WINDOW_COUNT) {
            loadedChunksRef.current = loadedChunksRef.current.slice(loadedChunksRef.current.length - MAX_CHUNK_WINDOW_COUNT);
        }

        syncChunkWindowContent();
        setLoadedBytes(chunkEndOffsetRef.current);
        setTotalBytes(chunkResponse.totalSize);
    };

    const initChunkLoad = async (currentPath: string, totalSize: number): Promise<boolean> => {
        isChunkModeRef.current = true;
        chunkPathRef.current = currentPath;
        chunkStartOffsetRef.current = 0;
        chunkEndOffsetRef.current = 0;
        chunkTotalSizeRef.current = totalSize;
        hasMoreChunkRef.current = true;
        hasPrevChunkRef.current = false;
        loadedChunksRef.current = [];
        lastEditorScrollTopRef.current = 0;
        lastPrevLoadTriggerAtRef.current = 0;
        lastNextLoadTriggerAtRef.current = 0;
        setLoadedBytes(0);
        setTotalBytes(totalSize);
        setContent('');

        await loadNextChunk();
        return true;
    };

    const getContent = async () => {
        setIsLoading(true);
        if (pane.modified) {
            try {
                const file = await editorDb?.getFileData(pane.key);
                setContent(file.content);
                setIsLoading(false);
                return
            } catch (e) {
                console.log("file not found in db")
            }
        }
        if (!path) {
            setIsLoading(false);
            return;
        }
        const fileInfo = await getFileNode(path ?? '');

        if (fileInfo?.size && fileInfo.size > LARGE_FILE_CHUNK_THRESHOLD) {
            setIsLargeFile(true);
            const chunkInited = await initChunkLoad(path, fileInfo.size);
            setLanguage('plaintext');
            if (chunkInited) {
                return;
            }
        }
        setIsLargeFile(false);

        resetChunkState();
        const content = await fileApi.getRawFileResource(path)
        if (content === undefined) {
            setIsLoading(false);
            return;
        }
        setContent(content);
        setIsLoading(false);
        setLanguage(getLanguageByFileName(fileName ?? ''));
    }

    // const [api, contextHolder] = notification.useNotification();

    // const openNotification = () => {

    //     const close = () => {
    //         showUpdateMessageRef.current = false;
    //         api.destroy();
    //     };

    //     const confirm = async()=>{
    //         await getContent()
    //         close()
    //     }

    //     if (showUpdateMessageRef.current) return;
    //     showUpdateMessageRef.current = true;
    //     const key = `open${Date.now()}`;
    //     const btn = (
    //         <Space>
    //             <Button type="primary" size="small" onClick={confirm}>
    //                 Refresh
    //             </Button>
    //         </Space>
    //     );
    //     api.open({
    //         message: 'File Updated',
    //         description:
    //             'The file has been updated by another process. Click refresh to load the latest version.',
    //         duration: 0,
    //         btn,
    //         key,
    //         onClose: close,
    //     });
    // }





    useEffect(() => {
        if (path) {
            fileEventQueue.setEventCallback(`${path}_editor`, async (events) => {
                const event = events.find(e => e.path === path && e.type === FileEventType.WRITE);
                if (event) {
                    getContent()
                    // openNotification()
                }
            })
        }


        return () => {
            console.log("unmount")
            resetChunkState();
            const editorMapService = FileEditorMapService.getInstance();
            editorMapService.deleteEditorRef(pane.key);
            try {
                editorDb?.deleteFileData(pane.key);
            } catch (e) {
                console.log("file not found in db")
            }
            if (path) {
                fileService.unListenPath([path])
            }
        }
    }, [])


    useEffect(() => {
        getContent();
    }, [fileName])

    const handleEditorChange = (value?: string) => {
        setContent(value);

        if (onChange) {
            onChange(value);
        }
    };

    useImperativeHandle(ref, () => ({
        getContent: () => content,
        refresh: async () => await getContent()
    }));

    const handleEditorMount = (editor: any, monaco: any) => {
        editorRef.current = editor;

        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {
            if (isChunkModeRef.current) {
                remoteSearchInputRef.current?.focus?.();
                return;
            }

            void editor.getAction('actions.find')?.run();
        });

        editor.addCommand(monaco.KeyCode.F3, () => {
            if (!isChunkModeRef.current) {
                void editor.getAction('editor.action.nextMatchFindAction')?.run();
                return;
            }
            void handleRemoteSearchNext();
        });

        editor.addCommand(monaco.KeyMod.Shift | monaco.KeyCode.F3, () => {
            if (!isChunkModeRef.current) {
                void editor.getAction('editor.action.previousMatchFindAction')?.run();
                return;
            }
            void handleRemoteSearchPrev();
        });

        editor.onDidScrollChange(() => {
            if (!isChunkModeRef.current) return;
            if (suppressScrollAutoLoadCountRef.current > 0) return;

            const scrollTop = editor.getScrollTop();
            const scrollHeight = editor.getScrollHeight();
            const layout = editor.getLayoutInfo();
            const viewportHeight = layout?.height ?? 0;
            const isScrollingUp = scrollTop < lastEditorScrollTopRef.current;
            const isScrollingDown = scrollTop > lastEditorScrollTopRef.current;
            lastEditorScrollTopRef.current = scrollTop;

            const nearBottom = scrollTop + viewportHeight + LOAD_MORE_SCROLL_THRESHOLD_PX >= scrollHeight;
            const nearTop = scrollTop <= LOAD_PREV_SCROLL_THRESHOLD_PX;
            const now = Date.now();

            if (
                nearBottom &&
                hasMoreChunkRef.current &&
                !isChunkLoadingRef.current &&
                (isScrollingDown || !hasPrevChunkRef.current) &&
                now - lastNextLoadTriggerAtRef.current >= CHUNK_SCROLL_TRIGGER_COOLDOWN_MS
            ) {
                lastNextLoadTriggerAtRef.current = now;
                void loadNextChunk();
            }

            if (
                nearTop &&
                hasPrevChunkRef.current &&
                !isChunkLoadingRef.current &&
                (isScrollingUp || nearTop) &&
                now - lastPrevLoadTriggerAtRef.current >= CHUNK_SCROLL_TRIGGER_COOLDOWN_MS
            ) {
                lastPrevLoadTriggerAtRef.current = now;
                void loadPrevChunk();
            }
        });
    };

    const pathItems = useMemo(() => {
        let currentPath = path;
        if (!currentPath) return [];
        const pathRoot = rootOfPath(roots, currentPath)?.path ?? fileService.baseHome;
        if (pathRoot) {
            currentPath = currentPath.replace(pathRoot, '~')
        }


        let pathList = currentPath.split('/');
        if (pathList.length > 3) {
            pathList = ["...", ...pathList.slice(-3)]
        }

        return pathList.map((segment, index, _arr) => ({
            key: index,
            title: segment || 'root',
            onClick: () => void (0)
        }))
    }, [path, roots])

    const loadProgressText = useMemo(() => {
        if (!isChunkModeRef.current || totalBytes <= 0) {
            return isLoading ? 'Loading...' : '';
        }

        const progress = Math.min(100, Math.round((loadedBytes / totalBytes) * 100));
        return isLoading ? `Loading ${progress}%...` : `Loaded ${progress}%`;
    }, [isLoading, loadedBytes, totalBytes]);

    const editorHeight = useMemo(() => {
        const toolbarHeight = isLargeFile
            ? EDITOR_TOOLBAR_HEIGHT + LARGE_FILE_EXTRA_TOOLBAR_HEIGHT
            : EDITOR_TOOLBAR_HEIGHT;
        return Math.max(size.height - toolbarHeight, EDITOR_MIN_HEIGHT);
    }, [size.height, isLargeFile]);

    useEffect(() => {
        if (!isChunkModeRef.current) {
            clearRemoteSearchState();
        }
    }, [isLargeFile]);

    useEffect(() => {
        if (!isLargeFile || !isChunkModeRef.current) {
            return;
        }

        const query = remoteSearchQuery.trim();
        if (!query) {
            clearRemoteSearchState();
            return;
        }

        if (remoteSearchDebounceTimerRef.current) {
            window.clearTimeout(remoteSearchDebounceTimerRef.current);
        }

        remoteSearchDebounceTimerRef.current = window.setTimeout(() => {
            void runRemoteSearch();
        }, REMOTE_SEARCH_DEBOUNCE_MS);

        return () => {
            if (remoteSearchDebounceTimerRef.current) {
                window.clearTimeout(remoteSearchDebounceTimerRef.current);
                remoteSearchDebounceTimerRef.current = undefined;
            }
        };
    }, [
        isLargeFile,
        remoteSearchQuery,
        remoteSearchCaseSensitive,
        remoteSearchWholeWord,
        remoteSearchRegex,
        path
    ]);

    useEffect(() => {
        if (!editorRef.current) {
            return;
        }

        // Force a relayout after container resize/tab switches to keep Monaco layers in sync.
        window.requestAnimationFrame(() => {
            editorRef.current?.layout?.();
        });
    }, [editorHeight, size.width, isLargeFile]);

    return (
        <div className='no-scrollbar flex flex-col'>
            <div className='w-full flex px-2 py-1 bg-white dark:bg-[#252526] items-center justify-between'>
                <div>
                    <Checkbox disabled={isLargeFile} className=' mr-2' checked={isWordWrap} onChange={e => setIsWordWrap(e.target.checked)}></Checkbox>
                    <span className=' mr-2'>Word Wrap</span>
                </div>
                <div>
                    {
                        path ? <Breadcrumb
                            separator=">"
                            items={pathItems} /> : null
                    }
                </div>
                <div className='flex items-center gap-2 min-w-[110px]'>
                    {
                        isLargeFile ? (
                            <Button size='small' loading={isJumpingToEnd} onClick={() => void scrollToFileEnd()}>
                                Scroll To End
                            </Button>
                        ) : null
                    }
                    <div className='text-xs text-gray-500 min-w-[110px] text-right'>
                        {loadProgressText}
                    </div>
                </div>
            </div>
            {
                isLargeFile ? (
                    <div className='w-full flex items-center gap-2 px-2 py-1 bg-white dark:bg-[#252526] border-t dark:border-[#404040]'>
                        <Input
                            ref={remoteSearchInputRef}
                            value={remoteSearchQuery}
                            onChange={e => setRemoteSearchQuery(e.target.value)}
                            onPressEnter={() => void runRemoteSearch()}
                            placeholder='Remote search in large file'
                            size='small'
                        />
                        <Button size='small' loading={isRemoteSearching} onClick={() => void runRemoteSearch()}>Search</Button>
                        <Button size='small' disabled={!remoteSearchMatchesRef.current.length} onClick={() => void handleRemoteSearchPrev()}>Prev</Button>
                        <Button size='small' disabled={!remoteSearchMatchesRef.current.length} onClick={() => void handleRemoteSearchNext()}>Next</Button>
                        <Checkbox checked={remoteSearchCaseSensitive} onChange={e => setRemoteSearchCaseSensitive(e.target.checked)}>Aa</Checkbox>
                        <Checkbox checked={remoteSearchWholeWord} onChange={e => setRemoteSearchWholeWord(e.target.checked)}>W</Checkbox>
                        <Checkbox checked={remoteSearchRegex} onChange={e => setRemoteSearchRegex(e.target.checked)}>.*</Checkbox>
                        <span className='text-xs text-gray-500 min-w-[90px] text-right'>
                            {remoteSearchTotal > 0 ? `${remoteSearchCurrent}/${remoteSearchTotal}` : '0/0'}
                        </span>
                    </div>
                ) : null
            }
            <ResizableContainer>
                <Editor
                    height={editorHeight}
                    width={size.width}
                    defaultLanguage=""
                    language={language}
                    value={content}
                    theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                    options={{
                        automaticLayout: true,
                        mouseStyle: 'text',
                        cursorStyle: 'line',
                        cursorBlinking: 'blink',
                        wordWrap: (isWordWrap || isLargeFile) ? "on" : "off",
                    }}
                    onChange={handleEditorChange}
                    onMount={handleEditorMount}
                />
            </ResizableContainer>

        </div>
    );

});

export default FileEditor;